/**
 * Compile-once SEO + viewer shell HTML for public/private shares (VISION).
 */
import type { PublicShareManifest } from '../types/snapshot.js';
import { renderSocialMetaTags, renderSearchConsoleVerificationMeta } from './socialShare.js';
import { renderAppCheckScriptTags } from './appCheckScripts.js';
import { renderPublicGa4ScriptTags } from './ga4Scripts.js';
import {
  buildCanvasListItems,
  canvasOgImageMediaId,
  canvasPageDescription,
  canvasPageTitle,
  isShareRootCanvas,
  itemsOnCanvas,
  shareCanvasHref,
  shareFullMediaUrl,
  shareRootHref,
  type SeoShareManifest,
} from './shareCanvasSeo.js';
import {
  resolveYouTubeEmbedUrl,
  youtubeThumbnailUrlFromId,
  youtubeVideoIdFromUrl,
  youtubeWatchUrlFromId,
} from './resolveYouTubeEmbedUrl.js';

export type ShareSiteConfig = {
  canonicalHost: string;
  softLaunchNoindex?: boolean;
  /** Extra noindex (e.g. public but awaiting listing approval). */
  forceNoindex?: boolean;
  reportApiUrl?: string;
  viewerEntry: string;
  /** Absolute or root-relative CSS for viewer (optional; may be linked from JS). */
  viewerCssEntry?: string | null;
  ogFallbackImage?: string | null;
  searchConsoleVerification?: string | null;
};

export type ContaindShareBootstrap = {
  schemaVersion: 1;
  visibility: 'public' | 'private';
  shareId: string;
  version: number;
  freezeVersion: number;
  slug: string | null;
  token: string | null;
  freezeUrl: string;
  mediaBaseUrl: string;
  reportApiUrl: string;
  canonicalUrl: string;
  softLaunchNoindex: boolean;
  theme: 'light' | 'dark';
  title: string;
  initialParentUuid: string | null;
  initialItemUuid: string | null;
  viewerEntry: string;
  /** True when rendered as a third-party embed (chrome-slim, poster-first). */
  embedded?: boolean;
  /** True when this Space may be embedded (owner opted in + actually allowed). */
  embeddable?: boolean;
};

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Canonical public gallery host (keep in sync with catalog-shared SHARES_PUBLIC_HOST). */
export const SHARES_PUBLIC_HOST = 'containd.space';
export const SHARES_PUBLIC_ORIGIN = `https://${SHARES_PUBLIC_HOST}`;

/** Browser tab title for a published Space. */
export function shareDocumentTitle(listTitle: string | null | undefined): string {
  const name = String(listTitle ?? '').trim() || 'Untitled';
  return `${name} — Containd Spaces`;
}

/** Same marks as lists/catalog `renderFaviconLinks`. */
export function shareFaviconLinksHtml(): string {
  return `<link rel="icon" type="image/png" href="${SHARES_PUBLIC_ORIGIN}/_assets/brand/containdLogoSmall.png" sizes="32x32"/>
<link rel="icon" type="image/png" href="${SHARES_PUBLIC_ORIGIN}/_assets/brand/logo_symmetrical_04.png" sizes="192x192"/>
<link rel="apple-touch-icon" href="${SHARES_PUBLIC_ORIGIN}/_assets/brand/logo_symmetrical_04.png"/>`;
}

const LEGAL_FOOTER_LINKS = [
  { href: 'https://catalog.containd.app/en/developers/', label: 'Docs' },
  { href: 'https://templates.containd.app/', label: 'Editor' },
  { href: 'https://www.nextlevelorder.com', label: 'Company' },
  { href: 'https://www.nextlevelorderengineering.com/privacy', label: 'Privacy' },
  { href: 'https://www.nextlevelorderengineering.com/ToS', label: 'Terms' },
  { href: 'https://www.nextlevelorderengineering.com/impressum', label: 'Imprint' },
  { href: `${SHARES_PUBLIC_ORIGIN}/legal/report/`, label: 'Report content' },
  { href: `${SHARES_PUBLIC_ORIGIN}/legal/contact/`, label: 'Contact' },
] as const;

function legalFooterHtml(opts?: { reportHref?: string }): string {
  const links = LEGAL_FOOTER_LINKS.map((l) => {
    const href =
      l.label === 'Report content' && opts?.reportHref ? opts.reportHref : l.href;
    const extra =
      href.startsWith(`${SHARES_PUBLIC_ORIGIN}/legal/`) || href.startsWith('/legal/')
        ? ''
        : ' target="_blank" rel="noopener noreferrer"';
    return `<a href="${esc(href)}"${extra}>${esc(l.label)}</a>`;
  }).join('\n    ·\n    ');
  return `<footer class="legal-footer">
    <p class="copyright"><a href="${SHARES_PUBLIC_ORIGIN}/">Published with Containd</a></p>
    ${links}
  </footer>`;
}

function asSeoManifest(manifest: PublicShareManifest): SeoShareManifest {
  return manifest as unknown as SeoShareManifest;
}

export type ShareYouTubeVideo = {
  videoId: string;
  watchUrl: string;
  embedUrl: string;
  thumbnailUrl: string;
  name: string;
  itemUuid: string;
};

function walkImageHrefs(nodes: unknown[], out: string[]): void {
  for (const raw of nodes) {
    if (!raw || typeof raw !== 'object') continue;
    const node = raw as { type?: string; props?: Record<string, unknown>; children?: unknown[] };
    if (node.type === 'image' && typeof node.props?.href === 'string') {
      out.push(node.props.href);
    }
    if (Array.isArray(node.children)) walkImageHrefs(node.children, out);
  }
}

/** YouTube videos on this canvas from item linkOut + baked image hrefs. */
export function collectShareYouTubeVideos(
  manifest: PublicShareManifest,
  canvasParentUuid?: string | null,
): ShareYouTubeVideo[] {
  const seo = asSeoManifest(manifest);
  const canvasItems = itemsOnCanvas(seo, canvasParentUuid);
  const seen = new Set<string>();
  const videos: ShareYouTubeVideo[] = [];

  const add = (url: string | null | undefined, name: string, itemUuid: string) => {
    const id = youtubeVideoIdFromUrl(url ?? null);
    if (!id || seen.has(id)) return;
    const embed = resolveYouTubeEmbedUrl(url ?? null);
    if (!embed) return;
    seen.add(id);
    videos.push({
      videoId: id,
      watchUrl: youtubeWatchUrlFromId(id),
      embedUrl: embed,
      thumbnailUrl: youtubeThumbnailUrlFromId(id),
      name,
      itemUuid,
    });
  };

  const baked = manifest.bakedDescriptors ?? {};
  for (const item of canvasItems) {
    add(item.linkOut, item.title?.trim() || 'Untitled', item.uuid);
    const prefix = `${item.uuid}:`;
    for (const [key, nodes] of Object.entries(baked)) {
      if (!key.startsWith(prefix) || !Array.isArray(nodes)) continue;
      const hrefs: string[] = [];
      walkImageHrefs(nodes, hrefs);
      for (const href of hrefs) add(href, item.title?.trim() || 'Untitled', item.uuid);
    }
  }
  return videos;
}

function videoObjectJsonLd(video: ShareYouTubeVideo): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.name,
    url: video.watchUrl,
    contentUrl: video.watchUrl,
    embedUrl: video.embedUrl,
    thumbnailUrl: video.thumbnailUrl,
  };
}

export function buildShareCanonicalUrl(
  host: string,
  manifest: PublicShareManifest,
  canvasParentUuid?: string | null,
): string {
  return shareCanvasHref(host, asSeoManifest(manifest), canvasParentUuid);
}

export function buildShareBootstrap(
  manifest: PublicShareManifest,
  site: ShareSiteConfig,
  opts?: {
    initialParentUuid?: string | null;
    initialItemUuid?: string | null;
    embeddable?: boolean;
    embedded?: boolean;
  },
): ContaindShareBootstrap {
  const seo = asSeoManifest(manifest);
  const soft =
    Boolean(site.softLaunchNoindex) || manifest.visibility === 'private';
  const parent = opts?.initialParentUuid ?? null;
  const canonicalUrl = shareCanvasHref(site.canonicalHost, seo, parent);
  const mediaBaseUrl = `/m/${encodeURIComponent(manifest.shareId)}/v${manifest.version}`;
  const freezeUrl =
    manifest.visibility === 'public' && manifest.slug
      ? `/s/${encodeURIComponent(manifest.slug)}/freeze.json`
      : `/share/${encodeURIComponent(manifest.token || manifest.shareId)}/freeze.json`;
  return {
    schemaVersion: 1,
    visibility: manifest.visibility,
    shareId: manifest.shareId,
    version: manifest.version,
    freezeVersion: manifest.freezeVersion ?? 0,
    slug: manifest.slug,
    token: manifest.visibility === 'private' ? manifest.token : null,
    freezeUrl,
    mediaBaseUrl,
    reportApiUrl:
      site.reportApiUrl || 'https://api.containd.app/v1/publicShareReport',
    canonicalUrl,
    softLaunchNoindex: soft,
    theme: manifest.theme,
    title: canvasPageTitle(seo, parent),
    initialParentUuid: parent,
    initialItemUuid: opts?.initialItemUuid ?? null,
    viewerEntry: site.viewerEntry,
    embeddable: opts?.embeddable === true,
    embedded: opts?.embedded === true,
  };
}

function seoListHtml(
  host: string,
  manifest: PublicShareManifest,
  canvasParentUuid?: string | null,
): string {
  const { items } = buildCanvasListItems(host, asSeoManifest(manifest), canvasParentUuid);
  if (items.length === 0) return '';
  const videos = collectShareYouTubeVideos(manifest, canvasParentUuid);
  const videosByItem = new Map<string, ShareYouTubeVideo[]>();
  for (const video of videos) {
    const list = videosByItem.get(video.itemUuid) ?? [];
    list.push(video);
    videosByItem.set(video.itemUuid, list);
  }
  const lis = items
    .map((row) => {
      const itemVideos = videosByItem.get(row.item.uuid) ?? [];
      const seenImg = new Set(row.imageUrls);
      const ytImgs = itemVideos
        .filter((v) => !seenImg.has(v.thumbnailUrl))
        .map(
          (v) =>
            `<img src="${esc(v.thumbnailUrl)}" alt="${esc(row.title)}"/>`,
        )
        .join('');
      const imgs = row.imageUrls
        .map(
          (src) =>
            `<img src="${esc(src)}" alt="${esc(row.title)}"/>`,
        )
        .join('');
      const titleInner = row.nestHref
        ? `<a href="${esc(row.nestHref)}">${esc(row.title)}</a>`
        : esc(row.title);
      const linkOut = row.linkOutHref
        ? ` <a href="${esc(row.linkOutHref)}" rel="noopener noreferrer">${esc(row.linkOutHref)}</a>`
        : '';
      const extraWatch = itemVideos
        .filter((v) => v.watchUrl !== row.linkOutHref)
        .map(
          (v) =>
            ` <a href="${esc(v.watchUrl)}" rel="noopener noreferrer">${esc(v.watchUrl)}</a>`,
        )
        .join('');
      return `<li>${imgs}${ytImgs}<span>${titleInner}</span>${linkOut}${extraWatch}</li>`;
    })
    .join('');
  return `<ul>${lis}</ul>`;
}

function collectionJsonLd(
  host: string,
  manifest: PublicShareManifest,
  canonicalUrl: string,
  canvasParentUuid?: string | null,
  image?: string | null,
): Record<string, unknown> {
  const seo = asSeoManifest(manifest);
  const { items } = buildCanvasListItems(host, seo, canvasParentUuid);
  const pageTitle = canvasPageTitle(seo, canvasParentUuid);
  const element = items.map((row, index) => {
    const images = row.imageUrls.length === 1 ? row.imageUrls[0] : row.imageUrls;
    return {
      '@type': 'ListItem',
      position: index + 1,
      name: row.title,
      url: row.nestHref || canonicalUrl,
      ...(row.imageUrls.length ? { image: images } : {}),
    };
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: manifest.description,
    inLanguage: manifest.locale,
    url: canonicalUrl,
    ...(image ? { image } : {}),
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: element,
    },
  };
}

/**
 * Render SEO document + `#share-root` + bootstrap for the hashed viewer module.
 */
export function renderShareHtml(
  manifest: PublicShareManifest,
  site: ShareSiteConfig,
  opts?: {
    initialParentUuid?: string | null;
    initialItemUuid?: string | null;
    /** Bake the Embed affordance into the viewer chrome (owner opted in + allowed). */
    embeddable?: boolean;
  },
): string {
  const seo = asSeoManifest(manifest);
  const parent = opts?.initialParentUuid ?? null;
  const bootstrap = buildShareBootstrap(manifest, site, opts);
  const canvasItems = itemsOnCanvas(seo, parent);
  const emptyNested =
    !isShareRootCanvas(seo, parent) && canvasItems.length === 0;
  const blocked =
    bootstrap.softLaunchNoindex ||
    Boolean(site.forceNoindex) ||
    manifest.visibility === 'private';
  const robots = blocked
    ? 'noindex,nofollow'
    : emptyNested
      ? 'noindex,follow'
      : 'index,follow';
  const pageTitle = canvasPageTitle(seo, parent);
  const pageDescription = canvasPageDescription(seo, parent);
  const og = (() => {
    const mediaId = canvasOgImageMediaId(seo, parent);
    if (!mediaId) return null;
    const media = manifest.media?.find((m) => m.id === mediaId);
    return {
      url: shareFullMediaUrl(site.canonicalHost, seo, mediaId),
      alt: media?.alt || pageTitle,
      width: media?.width ?? null,
      height: media?.height ?? null,
    };
  })();

  const jsonLd = collectionJsonLd(
    site.canonicalHost,
    manifest,
    bootstrap.canonicalUrl,
    parent,
    og?.url ?? null,
  );
  const videoLdScripts = collectShareYouTubeVideos(manifest, parent)
    .map((video) => `<script type="application/ld+json">${JSON.stringify(videoObjectJsonLd(video)).replace(/</g, '\\u003c')}</script>`)
    .join('\n');

  const cssLink = site.viewerCssEntry
    ? `<link rel="stylesheet" href="${esc(site.viewerCssEntry)}"/>`
    : '';
  const reportParams = new URLSearchParams();
  reportParams.set('url', bootstrap.canonicalUrl);
  if (manifest.slug) reportParams.set('slug', manifest.slug);
  const reportHref = `${SHARES_PUBLIC_ORIGIN}/legal/report/?${reportParams.toString()}`;
  const listHtml = seoListHtml(site.canonicalHost, manifest, parent);
  const description =
    isShareRootCanvas(seo, parent) && manifest.description
      ? `<p>${esc(manifest.description)}</p>`
      : !isShareRootCanvas(seo, parent) && pageDescription && pageDescription !== pageTitle
        ? `<p>${esc(pageDescription)}</p>`
        : '';

  return `<!DOCTYPE html>
<html lang="${esc(manifest.locale || 'en')}" data-theme="${esc(manifest.theme)}">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${esc(shareDocumentTitle(pageTitle))}</title>
<meta name="description" content="${esc(pageDescription)}"/>
<meta name="robots" content="${robots}"/>
<link rel="canonical" href="${esc(bootstrap.canonicalUrl)}"/>
${renderSearchConsoleVerificationMeta(site.searchConsoleVerification)}
${renderSocialMetaTags({
  type: 'website',
  title: pageTitle,
  description: pageDescription,
  url: bootstrap.canonicalUrl,
  locale: manifest.locale || 'en',
  imageUrl: og?.url ?? site.ogFallbackImage ?? null,
  imageAlt: og?.alt ?? null,
  imageWidth: og?.width ?? null,
  imageHeight: og?.height ?? null,
})}
${cssLink}
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link href="https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,400;0,600;0,700;1,400&family=Quicksand:wght@600;700&family=Source+Sans+3:wght@400;600&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&family=Space+Mono&display=swap" rel="stylesheet"/>
${shareFaviconLinksHtml()}
<style>
  :root { color-scheme: ${manifest.theme === 'dark' ? 'dark' : 'light'}; }
  html, body {
    margin: 0;
    height: 100%;
    overflow: hidden;
    font-family: 'Source Sans 3', 'Segoe UI', system-ui, sans-serif;
    background: ${manifest.theme === 'dark' ? '#1d274d' : '#e0e1dd'};
    color: ${manifest.theme === 'dark' ? '#e0e1dd' : '#1d274d'};
  }
  .seo { max-width: 40rem; padding: 1.25rem 1.5rem; }
  .seo h1 { font-size: 1.5rem; margin: 0 0 0.5rem; }
  .seo p { margin: 0 0 0.75rem; line-height: 1.45; opacity: 0.9; }
  .seo ul { margin: 0; padding: 0; list-style: none; }
  .seo li { margin: 0 0 1rem; }
  .seo img { max-width: 100%; height: auto; display: block; margin: 0 0 0.35rem; }
  .legal-footer { margin-top: 1.5rem; padding-top: 0.75rem; border-top: 1px solid rgba(29,39,77,0.12); font-size: 0.75rem; line-height: 1.6; text-align: center; }
  .legal-footer .copyright { margin: 0 0 0.5rem; }
  .legal-footer a { color: inherit; padding: 0 0.35em; }
  #share-root {
    position: fixed;
    inset: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .seo[hidden] { display: none !important; }
  #share-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    font-size: 1rem;
    letter-spacing: 0.02em;
    opacity: 0.85;
    user-select: none;
  }
</style>
<script type="application/ld+json">${JSON.stringify(jsonLd).replace(/</g, '\\u003c')}</script>
${videoLdScripts}
</head>
<body>
<main class="seo" id="share-seo" hidden>
  <h1>${esc(pageTitle)}</h1>
  ${description}
  <p>Shared with Containd${manifest.owner?.alias ? ` by ${esc(manifest.owner.displayName || manifest.owner.alias)}` : ''}.</p>
  ${listHtml}
  ${legalFooterHtml({ reportHref })}
</main>
<div id="share-root">
  <div id="share-loading" role="status" aria-live="polite">Loading viewer…</div>
</div>
${renderPublicGa4ScriptTags()}
<script src="/_assets/auth-session.js"></script>
${renderAppCheckScriptTags()}
<script>window.__CONTAIND_SHARE__=${JSON.stringify(bootstrap).replace(/</g, '\\u003c')};</script>
<script type="module" src="${esc(site.viewerEntry)}"></script>
<script>
  // SEO stays in source (hidden from first paint so browsers skip crawl images).
  // Clear the loading shell only when the interactive viewer marks ready.
  (function () {
    var root = document.getElementById('share-root');
    if (!root) return;
    function clearLoading() {
      var loading = document.getElementById('share-loading');
      if (loading) loading.remove();
      var seo = document.getElementById('share-seo');
      if (seo) seo.hidden = true;
    }
    if (root.getAttribute('data-viewer-ready') === '1') {
      clearLoading();
      return;
    }
    var obs = new MutationObserver(function () {
      if (root.getAttribute('data-viewer-ready') === '1') {
        clearLoading();
        obs.disconnect();
      }
    });
    obs.observe(root, { attributes: true, attributeFilter: ['data-viewer-ready'] });
  })();
</script>
</body>
</html>
`;
}

const PUBLISH_CARD_MEDIA_ID = 'card';

/** Small embed poster media id: dedicated low-res poster, then card thumb, then card. */
export function embedPosterMediaId(manifest: {
  media?: Array<{ id: string }>;
}): string | null {
  const media = manifest.media || [];
  const has = (id: string) => media.some((m) => m.id === id);
  return (
    [
      `${PUBLISH_CARD_MEDIA_ID}_poster`,
      `${PUBLISH_CARD_MEDIA_ID}_thumb`,
      PUBLISH_CARD_MEDIA_ID,
    ].find(has) || null
  );
}

function embedPosterUrl(manifest: PublicShareManifest): string | null {
  const id = embedPosterMediaId(manifest);
  if (!id) return null;
  return `/m/${encodeURIComponent(manifest.shareId)}/v${manifest.version}/${encodeURIComponent(id)}`;
}

/**
 * Poster-first third-party embed shell. Loads only a small poster and a copy
 * of the share title; the viewer bundle, freeze and item media are fetched only
 * after the visitor activates the embed. Keeps breadcrumb + legal footer.
 */
export function renderShareEmbedHtml(
  manifest: PublicShareManifest,
  site: ShareSiteConfig,
  opts?: { embeddable?: boolean },
): string {
  const bootstrap = buildShareBootstrap(manifest, site, {
    embeddable: opts?.embeddable === true,
    embedded: true,
  });
  const seo = asSeoManifest(manifest);
  const title = canvasPageTitle(seo, null);
  const poster = embedPosterUrl(manifest);
  const listHref =
    manifest.visibility === 'public' && manifest.slug
      ? `${SHARES_PUBLIC_ORIGIN}/s/${encodeURIComponent(manifest.slug)}/`
      : bootstrap.canonicalUrl;
  const dark = manifest.theme === 'dark';
  const bg = dark ? '#1d274d' : '#e0e1dd';
  const fg = dark ? '#e0e1dd' : '#1d274d';

  return `<!DOCTYPE html>
<html lang="${esc(manifest.locale || 'en')}" data-theme="${esc(manifest.theme)}">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${esc(shareDocumentTitle(title))}</title>
<meta name="robots" content="noindex,nofollow"/>
<meta name="referrer" content="no-referrer-when-downgrade"/>
<link rel="canonical" href="${esc(bootstrap.canonicalUrl)}"/>
${shareFaviconLinksHtml()}
<style>
  html, body { margin: 0; height: 100%; overflow: hidden; background: ${bg}; color: ${fg};
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; }
  #share-root { position: fixed; inset: 0; display: flex; flex-direction: column; }
  .embed-bar { flex: 0 0 auto; padding: 0.5rem 0.75rem; font-size: 0.85rem; font-weight: 600;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .embed-bar a { color: inherit; text-decoration: none; }
  .embed-body { flex: 1 1 auto; min-height: 0; display: flex; align-items: center;
    justify-content: center; padding: 0.5rem; }
  .embed-poster { position: relative; width: 100%; height: 100%; border: 0; padding: 0;
    margin: 0; cursor: pointer; background: rgba(127,127,127,0.12); border-radius: 10px;
    overflow: hidden; display: flex; align-items: center; justify-content: center; }
  .embed-poster img { width: 100%; height: 100%; object-fit: contain; display: block; }
  .embed-affordance { position: absolute; width: 3rem; height: 3rem; border-radius: 999px;
    background: rgba(0,0,0,0.55); color: #fff; display: flex; align-items: center;
    justify-content: center; }
  .embed-affordance svg { width: 24px; height: 24px; display: block; }
  .embed-hint { position: absolute; bottom: 0.5rem; left: 0; right: 0; text-align: center;
    font-size: 0.75rem; opacity: 0.8; }
  .embed-footer { flex: 0 0 auto; padding: 0.4rem 0.75rem; font-size: 0.7rem;
    text-align: center; opacity: 0.85; border-top: 1px solid rgba(127,127,127,0.25); }
  .embed-footer a { color: inherit; padding: 0 0.35em; }
</style>
</head>
<body>
<div id="share-root">
  <div class="embed-bar"><a href="${esc(listHref)}" target="_blank" rel="noopener noreferrer">${esc(title)}</a></div>
  <div class="embed-body">
    <button type="button" class="embed-poster" id="embed-activate" aria-label="Open interactive canvas">
      ${poster ? `<img src="${esc(poster)}" alt="${esc(title)}" loading="lazy" decoding="async"/>` : ''}
      <span class="embed-affordance" aria-hidden="true" data-embed-icon="tap">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5C7 9.06 7.79 10.43 9 11.24zM18.84 15.87l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6C13 6.67 12.33 6 11.5 6S10 6.67 10 7.5v10.74c-3.6-.76-3.54-.75-3.67-.75-.31 0-.59.13-.79.33l-.79.8 4.94 4.94C9.96 23.83 10.34 24 10.75 24h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2C19.75 16.63 19.37 16.09 18.84 15.87z"/>
        </svg>
      </span>
      <span class="embed-hint">Explore</span>
    </button>
  </div>
  <footer class="embed-footer">
    <a href="${SHARES_PUBLIC_ORIGIN}/" target="_blank" rel="noopener noreferrer">Published with Containd</a>
    · <a href="${esc(listHref)}" target="_blank" rel="noopener noreferrer">View full list</a>
  </footer>
</div>
<script>window.__CONTAIND_SHARE__=${JSON.stringify(bootstrap).replace(/</g, '\\u003c')};</script>
<script>
  (function () {
    var boot = window.__CONTAIND_SHARE__;
    var btn = document.getElementById('embed-activate');
    function activate() {
      if (!boot || !boot.viewerEntry) return;
      if (window.__CONTAIND_EMBED_ACTIVE__) return;
      window.__CONTAIND_EMBED_ACTIVE__ = 1;
      var root = document.getElementById('share-root');
      if (root) root.innerHTML = '';
      var s = document.createElement('script');
      s.type = 'module';
      s.src = boot.viewerEntry;
      document.head.appendChild(s);
    }
    if (btn) btn.addEventListener('click', activate);
  })();
</script>
</body>
</html>
`;
}

/**
 * Embed kill page (global embedsEnabled off, or admin embed-disabled for the
 * list). Loads no canvas data; links back to the live list.
 */
export function renderShareEmbedDisabledHtml(opts?: {
  title?: string | null;
  listHref?: string | null;
}): string {
  const title = esc(String(opts?.title || '').trim() || 'This Space');
  const href = opts?.listHref || SHARES_PUBLIC_ORIGIN;
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<meta name="robots" content="noindex,nofollow"/>
<title>Embedding unavailable — Containd Spaces</title>
${shareFaviconLinksHtml()}
<style>
  html, body { margin: 0; height: 100%; overflow: hidden; }
  body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; background: #e0e1dd;
    color: #1d274d; display: flex; align-items: center; justify-content: center; text-align: center; }
  .box { padding: 1.5rem; max-width: 26rem; }
  h1 { font-size: 1.05rem; margin: 0 0 0.5rem; }
  p { margin: 0 0 0.75rem; font-size: 0.85rem; opacity: 0.85; line-height: 1.45; }
  a { color: inherit; }
</style>
</head>
<body>
  <div class="box">
    <h1>${title}</h1>
    <p>Embedding is currently unavailable for this Space. You can still open it on containd.space.</p>
    <p><a href="${esc(href)}" target="_blank" rel="noopener noreferrer">View this Space on containd.space →</a></p>
  </div>
</body>
</html>
`;
}

export function renderShareTombstoneHtml(title = 'Share unavailable'): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<meta name="robots" content="noindex,nofollow"/>
<title>${esc(shareDocumentTitle(title))}</title>
${shareFaviconLinksHtml()}
</head>
<body style="font-family:system-ui;padding:2rem;background:#e0e1dd;color:#1d274d">
  <h1>${esc(title)}</h1>
  <p>This Space is no longer available.</p>
  <p><a href="/">Search Containd</a></p>
</body>
</html>
`;
}

export { shareRootHref };
